import { useEffect, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';

// WhatsApp's touch gestures on one element: tap, long-press, swipe left, swipe right.
// Vertical scrolling is left to the browser (touch-action: pan-y).
//
// A side with one action (a message's swipe to reply) runs it when the finger lifts past
// the threshold. A side with two or more (a chat in the list, as on an iPhone) slides
// open to show its buttons and stays open until tapped or closed; a full swipe runs the
// first action, the one at the outer edge (Archive on the left swipe, Read or Unread on
// the right). As on an iPhone the buttons share the revealed strip from the first pixel,
// growing together, so the edge button is never hidden behind the row. The drag offset
// (and the strip's width, which is the same number) is the one value set inline, because
// it changes with every finger movement.

const THRESHOLD = 72;
const BUTTON_WIDTH = 74;
const FULL_SWIPE_EXTRA = 90;
const LONG_PRESS_MS = 450;
const MOVE_TOLERANCE = 10;

export interface SwipeAction {
  label: string;
  icon?: ReactNode;
  /** Colour of the revealed button: the edge action is the strong one, as in WhatsApp. */
  tone?: 'primary' | 'grey' | 'dark';
  run: () => void;
}

type Side = 'left' | 'right';

interface Props {
  className?: string;
  children: ReactNode;
  onTap?: () => void;
  onLongPress?: () => void;
  /** Revealed on the right as the row slides left. First = outer edge and full swipe. */
  left?: SwipeAction | SwipeAction[];
  /** Revealed on the left as the row slides right. First = outer edge and full swipe. */
  right?: SwipeAction | SwipeAction[];
  ariaLabel?: string;
  /** Messages: with a mouse the text can be highlighted and copied, so a drag selects instead of
      swiping, and right-click on a selection shows the browser's own Copy (Gary, 18 Oct). */
  selectable?: boolean;
}

function list(a: SwipeAction | SwipeAction[] | undefined): SwipeAction[] {
  return !a ? [] : Array.isArray(a) ? a : [a];
}

export function Swipeable({ className = '', children, onTap, onLongPress, left, right, ariaLabel, selectable = false }: Props) {
  const leftActs = list(left);
  const rightActs = list(right);
  const [dx, setDx] = useState(0);
  const [open, setOpen] = useState<Side | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const start = useRef<{ x: number; y: number; id: number; base: number } | null>(null);
  const mode = useRef<'idle' | 'pending' | 'swipe' | 'scroll' | 'long'>('idle');
  const timer = useRef<number | undefined>(undefined);
  // Set after a swipe, scroll or long-press, so the click that follows is not a tap.
  const suppressClick = useRef(false);

  const reveals = (side: Side) => (side === 'left' ? leftActs : rightActs).length > 1;
  const openWidth = (side: Side) => (side === 'left' ? leftActs : rightActs).length * BUTTON_WIDTH;

  function close() {
    setOpen(null);
    setDx(0);
  }

  // An open row closes when you touch anywhere else, as in WhatsApp.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) close();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  function clearTimer() {
    window.clearTimeout(timer.current);
    timer.current = undefined;
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (selectable && e.pointerType === 'mouse') return;
    start.current = { x: e.clientX, y: e.clientY, id: e.pointerId, base: open === 'left' ? -openWidth('left') : open === 'right' ? openWidth('right') : 0 };
    mode.current = 'pending';
    suppressClick.current = false;
    if (onLongPress && !open) {
      timer.current = window.setTimeout(() => {
        if (mode.current !== 'pending') return;
        mode.current = 'long';
        if (navigator.vibrate) navigator.vibrate(15);
        onLongPress();
      }, LONG_PRESS_MS);
    }
  }

  function limit(offset: number): number {
    if (offset < 0) {
      if (!leftActs.length) return 0;
      return Math.max(offset, reveals('left') ? -(openWidth('left') + FULL_SWIPE_EXTRA + 40) : -THRESHOLD * 1.4);
    }
    if (!rightActs.length) return 0;
    return Math.min(offset, reveals('right') ? openWidth('right') + FULL_SWIPE_EXTRA + 40 : THRESHOLD * 1.4);
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const s = start.current;
    if (!s || s.id !== e.pointerId) return;
    const mx = e.clientX - s.x;
    const my = e.clientY - s.y;
    if (mode.current === 'pending') {
      if (Math.abs(my) > MOVE_TOLERANCE && Math.abs(my) > Math.abs(mx)) {
        mode.current = 'scroll';
        clearTimer();
        return;
      }
      const target = s.base + mx;
      if (Math.abs(mx) > MOVE_TOLERANCE && Math.abs(mx) > Math.abs(my) && (s.base !== 0 || (target < 0 && leftActs.length) || (target > 0 && rightActs.length))) {
        mode.current = 'swipe';
        clearTimer();
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    }
    if (mode.current === 'swipe') setDx(limit(s.base + mx));
  }

  function finish(cancelled: boolean) {
    clearTimer();
    const m = mode.current;
    const offset = dx;
    const wasOpen = open;
    mode.current = 'idle';
    start.current = null;
    if (m !== 'pending') suppressClick.current = true;
    if (cancelled) { close(); return; }

    if (m !== 'swipe') {
      // A tap on an open row closes it instead of opening the chat.
      if (wasOpen) { suppressClick.current = true; close(); }
      return;
    }
    const side: Side | null = offset < 0 ? 'left' : offset > 0 ? 'right' : null;
    if (!side) { close(); return; }
    const acts = side === 'left' ? leftActs : rightActs;
    const dist = Math.abs(offset);
    if (!reveals(side)) {
      close();
      if (dist >= THRESHOLD) acts[0]?.run();
      return;
    }
    const w = openWidth(side);
    if (dist >= w + FULL_SWIPE_EXTRA) {
      close();
      acts[0]?.run();
    } else if (dist >= w / 2) {
      setOpen(side);
      setDx(side === 'left' ? -w : w);
    } else {
      close();
    }
  }

  // Taps arrive as a click, so mouse, touch, keyboard and screen readers all open the chat.
  function onClick() {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    onTap?.();
  }

  const side: Side | null = dx < 0 ? 'left' : dx > 0 ? 'right' : null;
  const acts = side === 'left' ? leftActs : side === 'right' ? rightActs : [];
  const multi = side ? reveals(side) : false;
  const full = side ? Math.abs(dx) >= (multi ? openWidth(side) + FULL_SWIPE_EXTRA : THRESHOLD) : false;
  // Buttons sit with the first (full swipe) action at the outer edge of the row.
  const shown = side === 'left' ? [...acts].reverse() : acts;

  return (
    <div ref={root} className={`chats-swipe ${className}`}>
      {side && !multi && acts[0] && (
        <div className={`chats-swipe-action chats-swipe-action--${side}${full ? ' chats-swipe-action--armed' : ''}`} aria-hidden="true">
          {acts[0].icon}
          <span>{acts[0].label}</span>
        </div>
      )}
      {side && multi && (
        <div
          className={`chats-swipe-buttons chats-swipe-buttons--${side}${full ? ' chats-swipe-buttons--full' : ''}${mode.current !== 'swipe' ? ' chats-swipe-buttons--rest' : ''}`}
          style={{ width: Math.abs(dx) }}
        >
          {shown.map((a) => (
            <button
              key={a.label}
              type="button"
              className={`chats-swipe-btn chats-swipe-btn--${a.tone ?? 'grey'}${a === acts[0] ? ' chats-swipe-btn--edge' : ''}`}
              tabIndex={open ? 0 : -1}
              onClick={() => { close(); a.run(); }}
            >
              {a.icon}
              <span>{a.label}</span>
            </button>
          ))}
        </div>
      )}
      <div
        className={`chats-swipe-body${mode.current !== 'swipe' ? ' chats-swipe-body--rest' : ''}`}
        style={dx !== 0 ? { transform: `translateX(${dx}px)` } : undefined}
        role={onTap ? 'button' : undefined}
        tabIndex={onTap ? 0 : undefined}
        aria-label={ariaLabel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={() => finish(false)}
        onPointerCancel={() => finish(true)}
        onContextMenu={(e) => {
          // Right-click on a computer, and Android's own long-press event: same as our long-press.
          if (!onLongPress) return;
          // Text highlighted with the mouse: leave the browser's menu, so Copy is there.
          if (selectable && String(window.getSelection() ?? '').trim()) return;
          e.preventDefault();
          if (mode.current !== 'long') {
            clearTimer();
            mode.current = 'long';
            suppressClick.current = true;
            onLongPress();
          }
        }}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap?.(); }
          if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) { e.preventDefault(); onLongPress?.(); }
          if (e.key === 'Escape' && open) close();
        }}
      >
        {children}
      </div>
    </div>
  );
}
